import React, { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";
import CardForm from "./CardForm";
import "./CardFormWrapper.css";

function CardFormWrapper({
    onAddItem,
    onCancel,
    cardData,
    user,
    isEdit = false,
}) {
    const hasInitialCategory = Boolean(cardData?.category_id);

    const [step, setStep] = useState(
        hasInitialCategory ? 2 : 1
    );

    const [selectedCategory, setSelectedCategory] = useState(
        cardData?.category_id || ""
    );

    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);

    const darkMode =
        document.body.classList.contains("dark-mode");

    /*
     * ============================================================
     * FETCH CATEGORIES
     * ============================================================
     */

    useEffect(() => {
        const fetchCategories = async () => {
            const { data, error } = await supabase
                .from("categories")
                .select("*")
                .eq("allowed_in_user_items", true);

            if (error) {
                console.error(
                    "Failed to fetch categories:",
                    error
                );

                setCategories([]);
                return;
            }

            setCategories(data || []);
        };

        fetchCategories();
    }, []);

    /*
     * ============================================================
     * INITIAL CATEGORY
     *
     * If HobbliVision supplied category_id, use it immediately.
     * Otherwise the user selects a category normally.
     * ============================================================
     */

    useEffect(() => {
        if (cardData?.category_id) {
            setSelectedCategory(cardData.category_id);
            setStep(2);
        }
    }, [cardData?.category_id]);

    /*
     * ============================================================
     * FETCH SUBCATEGORIES
     * ============================================================
     */

    useEffect(() => {
        if (!selectedCategory) {
            setSubcategories([]);
            return;
        }

        const fetchSubcategories = async () => {
            const { data: subcatsData, error } =
                await supabase
                    .from("subcategories")
                    .select("*")
                    .eq("category_id", selectedCategory)
                    .eq("allowed_in_user_items", true);

            if (error) {
                console.error(
                    "Failed to fetch subcategories:",
                    error
                );

                setSubcategories([]);
                return;
            }

            if (!subcatsData) {
                setSubcategories([]);
                return;
            }

            const subcatsWithCounts =
                await Promise.all(
                    subcatsData.map(async (subcat) => {
                        const { count, error } =
                            await supabase
                                .from("user_items")
                                .select("*", {
                                    count: "exact",
                                    head: true,
                                })
                                .eq(
                                    "subcategory_id",
                                    subcat.id
                                );

                        return {
                            ...subcat,
                            item_count: error
                                ? 0
                                : count || 0,
                        };
                    })
                );

            subcatsWithCounts.sort(
                (a, b) =>
                    b.item_count - a.item_count
            );

            setSubcategories(
                subcatsWithCounts
            );
        };

        fetchSubcategories();
    }, [selectedCategory]);

    /*
     * ============================================================
     * CATEGORY SELECTION
     * ============================================================
     */

    const handleCategoryChange = (categoryId) => {
        setSelectedCategory(categoryId);
        setSubcategories([]);
    };

    /*
     * ============================================================
     * RENDER
     * ============================================================
     */

    return (
        <div
            className={`card-form-wrapper ${
                darkMode ? "dark" : ""
            }`}
        >
            <div className="modal-overlay">
                <div className="modal-content card-form-modal">

                    {/* ====================================================
                        STEP 1 — CATEGORY SELECTION
                    ==================================================== */}

                    {step === 1 && (
                        <div className="form-step">

                            <h3>Select Category</h3>

                            <select
                                value={selectedCategory}
                                onChange={(e) =>
                                    handleCategoryChange(
                                        e.target.value
                                    )
                                }
                                required
                            >
                                <option value="">
                                    Select category
                                </option>

                                {categories.map((category) => (
                                    <option
                                        key={category.id}
                                        value={category.id}
                                    >
                                        {category.name}
                                    </option>
                                ))}
                            </select>

                            <div className="modal-actions">

                                <button
                                    className="submit-btn"
                                    disabled={
                                        !selectedCategory
                                    }
                                    onClick={() =>
                                        setStep(2)
                                    }
                                >
                                    Next
                                </button>

                                <button
                                    className="cancel-btn"
                                    onClick={onCancel}
                                >
                                    Cancel
                                </button>

                            </div>
                        </div>
                    )}

                    {/* ====================================================
                        STEP 2 — CARD FORM
                    ==================================================== */}

                    {step === 2 && (
                        <CardForm
                            cardData={{
                                ...(cardData || {}),
                                category_id:
                                    cardData?.category_id ||
                                    selectedCategory,
                            }}
                            user={user}
                            onAddItem={onAddItem}
                            onCancel={onCancel}
                            categories={categories}
                            subcategories={subcategories}
                            initialCategory={
                                selectedCategory
                            }
                            isEdit={isEdit}
                        />
                    )}

                </div>
            </div>
        </div>
    );
}

export default CardFormWrapper;